/**
 * One background job run — what a single fire did.
 *
 * Opened from the job page, the schedule page, the run history grid, or straight
 * after Run now. The URL names any attempt; the page shows the whole fire, since
 * one fire of a job with retries writes one document per attempt.
 *
 * It polls while the run is still going, so pressing Run now and watching the
 * result arrive works without refreshing.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useToastApi } from '../../hooks/use-toast-api';
import { backgroundJobService } from '../../services/background-job.service';
import type { BackgroundJobRun } from '../../types/background-job.types';
import { JsonView } from '../../components/common/json-view';
import { extractErrorMessage } from '../../utils/error-handler';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, WarnBanner,
} from '../../components/notifications/engine-view-ui';
import { useJobPermissions } from './tabs/use-job-permissions';
import { RUN_STATUS_SEVERITY, isLiveStatus, runDuration } from './run-format';
import { jobPath, runPath, schedulePath } from './view-paths';

/** How often a run still in progress is re-read. */
const LIVE_POLL_MS = 5000;

const BackgroundRunView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { canExecute, canReadRuns } = useJobPermissions();

  const runQ = useQuery({
    queryKey: ['bg-run', id],
    queryFn: () => backgroundJobService.getRun(id!),
    enabled: !!id && canReadRuns,
    refetchInterval: (query) =>
      (isLiveStatus((query.state.data as BackgroundJobRun | undefined)?.status) ? LIVE_POLL_MS : false),
  });
  const run = runQ.data;

  // Every attempt of this fire, fetched by correlation id: they need not be
  // adjacent in any list, and the URL may name an early attempt of a later retry.
  const attemptsQ = useQuery({
    queryKey: ['bg-run-attempts', run?.runCorrelationId],
    queryFn: () => backgroundJobService.listRuns({
      runCorrelationId: run!.runCorrelationId, page: 1, page_size: 100,
    }),
    enabled: !!run?.runCorrelationId,
    refetchInterval: run && isLiveStatus(run.status) ? LIVE_POLL_MS : false,
  });
  const attempts = useMemo(() => {
    const rows = attemptsQ.data?.items?.length ? attemptsQ.data.items : (run ? [run] : []);
    return [...rows].sort((a, b) => a.attemptNumber - b.attemptNumber);
  }, [attemptsQ.data, run]);
  const final = attempts[attempts.length - 1] ?? run;
  const first = attempts[0] ?? run;

  const jobQ = useQuery({
    queryKey: ['bg-job', run?.jobId],
    queryFn: () => backgroundJobService.getJob(run!.jobId),
    enabled: !!run?.jobId,
    staleTime: 60_000,
  });
  const schedulesQ = useQuery({
    queryKey: ['bg-schedules', run?.jobId],
    queryFn: () => backgroundJobService.listSchedules(run!.jobId),
    enabled: !!run?.jobScheduleId,
    staleTime: 60_000,
  });
  const schedule = (schedulesQ.data ?? []).find((s) => s._id === run?.jobScheduleId);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bg-run'] });
    qc.invalidateQueries({ queryKey: ['bg-run-attempts'] });
    qc.invalidateQueries({ queryKey: ['bg-runs'] });
  };

  const stopM = useMutation({
    mutationFn: () => backgroundJobService.stopRun(final!._id),
    onSuccess: (r) => {
      // A request, not the stopped state: the instance running it acts on it.
      ok(r.stopRequested
        ? 'Stop requested — it ends once the instance running it acts on the request'
        : `Nothing to stop — it had already finished (${r.status})`);
      refresh();
    },
    onError: (e) => fail(e),
  });

  // The resolved parameters already include the job's defaults and any schedule
  // parameters, so re-running with them reproduces this fire's input exactly.
  const rerunM = useMutation({
    mutationFn: () => backgroundJobService.runNow(run!.jobId, first?.inputParameters || {}),
    onSuccess: (next) => {
      ok('Run started with the same parameters');
      refresh();
      if (next?._id) navigate(runPath(next._id));
    },
    onError: (e) => fail(e),
  });

  if (!canReadRuns) {
    return (
      <div className="p-4">
        <Message severity="warn" className="w-full" text="Run history needs BACKGROUND_JOB_RUN.READ, which you do not have." />
      </div>
    );
  }
  if (runQ.isLoading) {
    return <div className="flex min-h-[300px] items-center justify-center"><ProgressSpinner style={{ width: 40, height: 40 }} /></div>;
  }
  if (runQ.isError || !run || !final || !first) {
    return (
      <div className="p-4">
        {toast}
        <Message severity="error" text={extractErrorMessage(runQ.error, 'Run not found')} className="mb-4 w-full" />
        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigate('/background-jobs?tab=jobs')} />
      </div>
    );
  }

  const job = jobQ.data;
  const live = isLiveStatus(final.status);
  const manual = run.triggeredBy === 'MANUAL_USER';

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-BackgroundRun-View">
      {toast}
      <BackLink label={job ? job.jobName : 'Background job'} onClick={() => navigate(jobPath(run.jobId))} />

      <Hero
        icon="zap"
        chip="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400"
        code={job?.jobKey || run.jobId}
        tags={<>
          <Tag value={final.status} severity={RUN_STATUS_SEVERITY[final.status]} />
          <Tag value={manual ? 'Manual' : 'Scheduler'} severity={manual ? 'info' : 'secondary'} />
          {attempts.length > 1 && <Tag value={`${attempts.length} attempts`} severity="warning" />}
        </>}
        description={<>
          {job ? <span className="font-medium text-gray-700 dark:text-gray-200">{job.jobName}</span> : null}
          {first.startedAt ? ` — started ${formatTimestamp(first.startedAt)}` : ''}
        </>}
        actions={<>
          {canExecute && live && !final.cancelRequested && (
            <Button label="Stop" icon="pi pi-stop-circle" size="small" severity="danger" outlined
              onClick={() => stopM.mutate()} disabled={stopM.isPending} />
          )}
          {canExecute && !live && (
            <Button label="Run again" icon="pi pi-replay" size="small" severity="success" outlined
              onClick={() => rerunM.mutate()} disabled={rerunM.isPending} />
          )}
          <Button label="Open job" icon="pi pi-cog" size="small" severity="secondary"
            onClick={() => navigate(jobPath(run.jobId))} />
        </>}
        banner={final.cancelRequested && live
          ? <WarnBanner text="Stop requested — it ends once the instance running it acts on the request." />
          : live
            ? <ActiveBanner text="Still in progress — this page refreshes every few seconds. RUNNING means not yet finalised; an abandoned run is swept to FAILED when its lock expires." />
            : final.status === 'FAILED'
              ? <WarnBanner text={final.logSummary || 'This run failed. See the attempts below.'} />
              : null}
      />

      <div className={CARD}>
        <CardHeader icon="info" color="text-sky-400" title="Summary" />
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-2">
          <Field label="Job">
            <button type="button" onClick={() => navigate(jobPath(run.jobId))}
              className="text-blue-600 hover:underline dark:text-blue-400">
              {job ? `${job.jobName} (${job.jobKey})` : run.jobId}
            </button>
          </Field>
          <Field label="Schedule">
            {run.jobScheduleId ? (
              <button type="button" onClick={() => navigate(schedulePath({ _id: run.jobScheduleId!, jobId: run.jobId }))}
                className="text-blue-600 hover:underline dark:text-blue-400">
                {schedule?.scheduleName || run.jobScheduleId}
              </button>
            ) : 'None — started manually'}
          </Field>
          <Field label="Started">{first.startedAt ? formatTimestamp(first.startedAt) : '—'}</Field>
          <Field label="Ended">{final.endedAt ? formatTimestamp(final.endedAt) : live ? 'Still running' : '—'}</Field>
          <Field label="Duration">{runDuration({ startedAt: first.startedAt, endedAt: final.endedAt })}</Field>
          <Field label="Attempts">{attempts.length}</Field>
          <Field label="Correlation id" mono>{run.runCorrelationId}</Field>
          {final.errorLogId && <Field label="Error log" mono>{final.errorLogId}</Field>}
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="list" color="text-indigo-400" title={`Attempts (${attempts.length})`}
          hint={attempts.length > 1 ? 'Each retry is its own attempt' : undefined} />
        <div className="flex flex-col gap-3 px-4 py-4">
          {attempts.map((a) => (
            <div key={a._id} className={`rounded-lg border p-3 ${a._id === id
              ? 'border-blue-300 dark:border-blue-700'
              : 'border-line'}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Tag value={`Attempt ${a.attemptNumber}`} severity="secondary" />
                <Tag value={a.status} severity={RUN_STATUS_SEVERITY[a.status]} />
                <span className="text-xs text-ink-muted">
                  {a.startedAt ? formatTimestamp(a.startedAt) : '—'} · {runDuration(a)}
                </span>
                {/* only the FINAL failed attempt carries an error log */}
                {a.errorLogId && <Tag value="error logged" severity="danger" />}
                {a.cancelRequested && <Tag value="stop requested" severity="warning" />}
              </div>
              {a.logSummary && <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">{a.logSummary}</p>}
              {a.cancelReason && <p className="mb-2 text-xs text-gray-500">Stop reason: {a.cancelReason}</p>}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <JsonView label="Resolved parameters" value={a.inputParameters} />
                <JsonView label="Output" value={a.outputData} />
              </div>
              {a.resolvedExecutionPayload && (
                <JsonView className="mt-3" label="What was executed" value={a.resolvedExecutionPayload} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BackgroundRunView;
